import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Alert, Row, Col, Card, Badge } from 'react-bootstrap';
import { useDeliveryConfiguration } from '../../../hooks/useDeliveryConfiguration';
import { validateInput, validateObject } from '../../../utils/sanitizer';
import LoadingSpinner from '../../shared/LoadingSpinner';

/**
 * DeliveryConfigurationSection - Section component for managing delivery configuration
 * 
 * @param {string} customerId - The ID of the customer
 */
const DeliveryConfigurationSection = ({ customerId }) => {
  const {
    config,
    loading,
    error: apiError,
    hasExistingApiKey,
    createConfig,
    updateConfig,
    clearError
  } = useDeliveryConfiguration(customerId);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const [configForm, setConfigForm] = useState({
    deliveryUrl: '',
    apiSigningKey: '',
    enabled: true,
    retryAttempts: 3,
    timeoutSeconds: 30,
    retryDelaySeconds: 60
  });

  const timeoutRef = useRef(null);
  /**
   * Load configuration data into form when available
   */
  useEffect(() => {
    if (config) {
      setConfigForm({
        deliveryUrl: config.deliveryUrl || '',
        apiSigningKey: '',
        enabled: config.enabled ?? true,
        retryAttempts: config.retryAttempts ?? 3,
        timeoutSeconds: config.timeoutSeconds ?? 30,
        retryDelaySeconds: config.retryDelaySeconds ?? 60
      });
    }
  }, [config]);

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Handle form submission
   */
  const handleSave = async (e) => {
    e.preventDefault();

    // Validate input
    const validation = validateObject(configForm);
    if (!validation.isValid) {
      setError('Input contains potentially unsafe content.');
      return;
    }

    if (!configForm.deliveryUrl.trim()) {
      setError('Delivery URL is required.');
      return;
    }

    // Require API signing key for new configurations
    if (!config && !configForm.apiSigningKey.trim()) {
      setError('API Signing Key is required for new configurations.');
      return;
    }

    // Validate URL format
    try {
      const parsedUrl = new URL(configForm.deliveryUrl);
      // Allow HTTP for localhost/127.0.0.1 for local development, require HTTPS otherwise
      const isLocalhost = parsedUrl.hostname === 'localhost' || 
                         parsedUrl.hostname === '127.0.0.1' || 
                         parsedUrl.hostname === '[::1]';
      
      if (parsedUrl.protocol !== 'https:' && !isLocalhost) {
        setError('Delivery URL must use HTTPS protocol for secure data transmission (HTTP is only allowed for localhost).');
        return;
      }
    } catch {
      setError('Please enter a valid URL.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (config) {
        await updateConfig(configForm);
        setSuccess('Delivery configuration updated successfully!');
      } else {
        await createConfig(configForm);
        setSuccess('Delivery configuration created successfully!');
      }

      // Clear the API key field after save
      setConfigForm(prev => ({ ...prev, apiSigningKey: '' }));
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save delivery configuration');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle form field changes
   */
  const handleChange = (field, value) => {
    if (typeof value === 'string' && !validateInput(value)) {
      setError('Input contains potentially unsafe content.');
      return;
    }
    setConfigForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      {(error || apiError) && (
        <Alert variant="danger" onClose={() => { setError(''); clearError(); }} dismissible>
          {error || apiError}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      <Card>
        <Card.Header>
          <h5 className="mb-0">Delivery Configuration</h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSave}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Delivery URL *</Form.Label>
                  <Form.Control
                    type="url"
                    value={configForm.deliveryUrl}
                    onChange={(e) => handleChange('deliveryUrl', e.target.value)}
                    placeholder="https://api.example.com/webhook"
                    required
                  />
                  <Form.Text className="text-muted">
                    HTTPS endpoint for invoice data delivery
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    API Signing Key {hasExistingApiKey && <Badge bg="success">Key Configured</Badge>}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    value={configForm.apiSigningKey}
                    onChange={(e) => handleChange('apiSigningKey', e.target.value)}
                    placeholder={hasExistingApiKey ? 'Leave blank to keep existing key' : 'Enter signing key'}
                    autoComplete="new-password"
                  />
                  <Form.Text className="text-muted">
                    {hasExistingApiKey 
                      ? 'Leave blank to keep existing key. Enter a new key to update it.'
                      : 'Secret key for HMAC authentication'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Enabled</Form.Label>
                  <Form.Select
                    value={configForm.enabled}
                    onChange={(e) => handleChange('enabled', e.target.value === 'true')}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Enable or disable delivery to this endpoint
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Retry Attempts</Form.Label>
                  <Form.Control
                    type="number"
                    value={configForm.retryAttempts}
                    onChange={(e) => handleChange('retryAttempts', parseInt(e.target.value, 10) || 0)}
                    min="0"
                    max="10"
                  />
                  <Form.Text className="text-muted">
                    Number of retry attempts for failed deliveries (0-10)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Timeout (seconds)</Form.Label>
                  <Form.Control
                    type="number"
                    value={configForm.timeoutSeconds}
                    onChange={(e) => handleChange('timeoutSeconds', parseInt(e.target.value, 10) || 1)}
                    min="1"
                    max="300"
                  />
                  <Form.Text className="text-muted">
                    Request timeout in seconds (1-300)
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Retry Delay (seconds)</Form.Label>
                  <Form.Control
                    type="number"
                    value={configForm.retryDelaySeconds}
                    onChange={(e) => handleChange('retryDelaySeconds', parseInt(e.target.value, 10) || 1)}
                    min="1"
                    max="900"
                  />
                  <Form.Text className="text-muted">
                    Delay between retry attempts in seconds (1-900)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex gap-2">
              <Button
                type="submit"
                variant="primary"
                disabled={saving || !configForm.deliveryUrl.trim()}
              >
                {saving ? 'Saving...' : config ? 'Update Configuration' : 'Create Configuration'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

DeliveryConfigurationSection.propTypes = {
  customerId: PropTypes.string.isRequired
};

export default DeliveryConfigurationSection;
